import React, { useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Mail,
  Lock,
  User,
  Home,
  Briefcase,
  Building2,
  CheckCircle,
  CreditCard,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { useAuth } from "../App";
import Header from "../components/Header";
import { api, extractApiErrorMessage } from "../services/apiClient";
import { MICROCOPY } from "../constants/microcopy";
import { getTrialDaysCopy } from "../constants/plans";
import { getActivationJourneySteps } from "../constants/activationJourney";

const ROLE_OPTIONS = [
  {
    value: "basic",
    label: "Usuario Básico",
    description: "Explora propiedades y desarrollos, guarda favoritos y publica 1 propiedad",
    plan: "free",
    planLabel: "Plan Gratuito",
    price: "Gratis",
    icon: User,
    color: "bg-slate-50 border-slate-200 hover:border-slate-400",
    selectedColor: "bg-slate-100 border-slate-600 ring-2 ring-slate-400",
    badgeColor: "bg-slate-100 text-slate-700",
  },
  {
    value: "owner",
    label: "Propietario",
    description: "Publica hasta 10 propiedades y destaca hasta 5 activas",
    plan: "pro",
    planLabel: "Plan Pro",
    price: "$450 MXN/mes",
    icon: Home,
    color: "bg-amber-50 border-amber-200 hover:border-amber-500",
    selectedColor: "bg-amber-50 border-amber-600 ring-2 ring-amber-400",
    badgeColor: "bg-amber-100 text-amber-800",
  },
  {
    value: "broker",
    label: "Asesor Inmobiliario",
    description: "Representa propiedades, opera leads y registra comisiones",
    plan: "broker",
    planLabel: "Plan Asesor",
    price: "$999 MXN/mes",
    icon: Briefcase,
    color: "bg-blue-50 border-blue-200 hover:border-blue-500",
    selectedColor: "bg-blue-50 border-blue-600 ring-2 ring-blue-400",
    badgeColor: "bg-blue-100 text-blue-800",
  },
  {
    value: "developer",
    label: "Desarrolladora",
    description: "Gestiona desarrollos, unidades e inventario para brokers",
    plan: "developer",
    planLabel: "Plan Desarrolladora",
    price: "$2,500 MXN/mes",
    icon: Building2,
    color: "bg-emerald-50 border-emerald-200 hover:border-emerald-500",
    selectedColor: "bg-emerald-50 border-emerald-600 ring-2 ring-emerald-400",
    badgeColor: "bg-emerald-100 text-emerald-800",
  },
];

const Register = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login } = useAuth();
  const initialRole = searchParams.get("role");
  const defaultRole = ROLE_OPTIONS.some(
    (option) => option.value === initialRole,
  )
    ? initialRole
    : "basic";

  const [formData, setFormData] = useState({
    email: "",
    password: "",
    name: "",
    role: defaultRole,
    acceptTerms: false,
  });
  const [loading, setLoading] = useState(false);

  const selectedRole = ROLE_OPTIONS.find((r) => r.value === formData.role);
  const isPaidPlan = selectedRole && selectedRole.plan !== "free";
  const activationJourneySteps = getActivationJourneySteps({
    planLabel: selectedRole?.planLabel,
    trialDaysCopy: getTrialDaysCopy(),
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await api.post("/auth/register", {
        email: formData.email.trim().toLowerCase(),
        password: formData.password,
        name: formData.name.trim(),
        role: formData.role,
      });

      const responseText = await response.text();
      let data = null;
      try {
        data = responseText ? JSON.parse(responseText) : null;
      } catch {
        data = null;
      }

      if (!response.ok) {
        throw new Error(extractApiErrorMessage(data, "Error al registrarse"));
      }

      login(data.access_token, data.user);
      toast.success("¡Registro exitoso! Bienvenido a Metal Concrete.");
      if (isPaidPlan) {
        navigate("/account?tab=subscription");
      } else {
        navigate("/dashboard");
      }
    } catch (error) {
      toast.error(error?.message || "Error al registrarse");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen">
      <Header />
      <div className="grid grid-cols-1 lg:grid-cols-2 min-h-[calc(100vh-80px)]">
        <motion.div
          initial={{ opacity: 0, x: -30 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6 }}
          className="flex items-center justify-center p-8 md:p-12"
        >
          <div className="w-full max-w-md">
            <div className="mb-8">
              <h1
                className="text-4xl md:text-5xl font-bold mb-4 tracking-tight"
                style={{ fontFamily: "Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif" }}
                data-testid="register-title"
              >
                Crea tu cuenta
              </h1>
              <p className="text-muted-foreground">
                {MICROCOPY.auth.register.subtitle}
              </p>
            </div>

            <form
              onSubmit={handleSubmit}
              className="space-y-6"
              data-testid="register-form"
            >
              {/* Name */}
              <div>
                <label className="text-sm font-medium mb-2 block">
                  Nombre Completo
                </label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                  <Input
                    type="text"
                    placeholder="Tu nombre"
                    value={formData.name}
                    onChange={(e) =>
                      setFormData({ ...formData, name: e.target.value })
                    }
                    className="pl-10"
                    required
                    data-testid="name-input"
                  />
                </div>
              </div>

              {/* Email */}
              <div>
                <label className="text-sm font-medium mb-2 block">
                  Correo Electrónico
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                  <Input
                    type="email"
                    placeholder="tu@email.com"
                    value={formData.email}
                    onChange={(e) =>
                      setFormData({ ...formData, email: e.target.value })
                    }
                    className="pl-10"
                    required
                    data-testid="email-input"
                  />
                </div>
              </div>

              {/* Password */}
              <div>
                <label className="text-sm font-medium mb-2 block">
                  Contraseña
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                  <Input
                    type="password"
                    placeholder="••••••••"
                    value={formData.password}
                    onChange={(e) =>
                      setFormData({ ...formData, password: e.target.value })
                    }
                    className="pl-10"
                    required
                    data-testid="password-input"
                  />
                </div>
              </div>

              {/* Role selection — card grid */}
              <div>
                <label className="text-sm font-medium mb-3 block">
                  Tipo de cuenta
                </label>
                <p className="text-xs text-muted-foreground mb-3">
                  {MICROCOPY.auth.register.roleHint}
                </p>
                <div
                  className="grid grid-cols-2 gap-3"
                  data-testid="role-select"
                >
                  {ROLE_OPTIONS.map((option) => {
                    const Icon = option.icon;
                    const isSelected = formData.role === option.value;
                    return (
                      <button
                        key={option.value}
                        type="button"
                        onClick={() =>
                          setFormData({ ...formData, role: option.value })
                        }
                        className={`relative flex flex-col items-start gap-1 p-3 rounded-lg border-2 text-left transition-all cursor-pointer ${
                          isSelected ? option.selectedColor : option.color
                        }`}
                        data-testid={`role-option-${option.value}`}
                      >
                        {isSelected && (
                          <CheckCircle className="absolute top-2 right-2 w-4 h-4 text-primary" />
                        )}
                        <Icon className="w-5 h-5 text-current mb-1" />
                        <span className="font-semibold text-sm leading-tight">
                          {option.label}
                        </span>
                        <span className="text-xs text-muted-foreground leading-tight">
                          {option.description}
                        </span>
                        <span
                          className={`mt-1 text-xs font-medium px-2 py-0.5 rounded-full ${option.badgeColor}`}
                        >
                          {option.price}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Plan info banner */}
              {selectedRole && (
                <div
                  className={`flex items-start gap-3 rounded-lg p-3 border ${isPaidPlan ? "bg-blue-50 border-blue-200" : "bg-green-50 border-green-200"}`}
                >
                  <CreditCard
                    className={`w-5 h-5 mt-0.5 flex-shrink-0 ${isPaidPlan ? "text-blue-600" : "text-green-600"}`}
                  />
                  <div>
                    <p className="text-sm font-medium">
                      {isPaidPlan
                        ? `Se asignará ${selectedRole.planLabel} (${selectedRole.price})`
                        : `Se asignará ${selectedRole.planLabel} — sin costo`}
                    </p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {isPaidPlan
                        ? MICROCOPY.auth.register.paidPlanNote
                        : MICROCOPY.auth.register.freePlanNote}
                    </p>
                    {isPaidPlan && (
                      <p className="text-xs font-medium text-blue-700 mt-2">
                        Activas {selectedRole.planLabel} después del registro y, si eres elegible, inicias {getTrialDaysCopy()} sin repetirla más adelante.
                      </p>
                    )}
                  </div>
                </div>
              )}

              {isPaidPlan && (
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  {activationJourneySteps.map((step) => (
                    <div
                      key={step.key}
                      className="rounded-lg border border-border bg-muted/20 px-3 py-3"
                    >
                      <p className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground mb-1">{step.eyebrow}</p>
                      <p className="text-sm font-medium">{step.title}</p>
                    </div>
                  ))}
                </div>
              )}

              {/* Terms */}
              <div className="flex items-start gap-2">
                <input
                  type="checkbox"
                  checked={formData.acceptTerms}
                  onChange={(e) =>
                    setFormData({ ...formData, acceptTerms: e.target.checked })
                  }
                  required
                  className="mt-1"
                />
                <label className="text-sm">
                  Acepto los{" "}
                  <Link to="/terms" className="text-primary hover:underline">
                    Términos y Condiciones
                  </Link>{" "}
                  y el{" "}
                  <Link to="/privacy" className="text-primary hover:underline">
                    Aviso de Privacidad
                  </Link>
                </label>
              </div>

              <Button
                type="submit"
                className="w-full"
                disabled={loading}
                data-testid="register-button"
              >
                {loading
                  ? "Registrando..."
                  : isPaidPlan
                    ? "Crear cuenta y continuar a activación"
                    : "Crear cuenta gratuita"}
              </Button>
            </form>

            <p className="mt-6 text-center text-sm text-muted-foreground">
              ¿Ya tienes cuenta?{" "}
              <Link
                to="/login"
                className="font-medium text-primary hover:underline"
              >
                Inicia Sesión
              </Link>
            </p>
            <p className="mt-2 text-center text-sm text-muted-foreground">
              ¿Olvidaste tu contraseña?{" "}
              <Link
                to="/reset-password"
                className="font-medium text-primary hover:underline"
              >
                Recuperarla aquí
              </Link>
            </p>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, x: 30 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6 }}
          className="hidden lg:block relative overflow-hidden"
        >
          <div
            className="absolute inset-0"
            style={{
              backgroundImage:
                "url(https://images.pexels.com/photos/7414904/pexels-photo-7414904.jpeg)",
              backgroundSize: "cover",
              backgroundPosition: "center",
            }}
          >
            <div className="absolute inset-0 bg-gradient-to-br from-primary/80 to-secondary/80"></div>
          </div>
          <div className="relative z-10 flex items-center justify-center h-full p-12">
            <div className="text-center text-primary-foreground">
              <h2
                className="text-5xl font-bold mb-4 tracking-tight"
                style={{ fontFamily: "Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif" }}
              >
                Bienvenido a Metal Concrete
              </h2>
              <p className="text-xl opacity-90">
                La plataforma líder en bienes raíces premium
              </p>
              <div className="mt-10 grid grid-cols-2 gap-4 text-sm text-left">
                {ROLE_OPTIONS.map((opt) => {
                  const Icon = opt.icon;
                  return (
                    <div
                      key={opt.value}
                      className="bg-white/10 rounded-lg p-3 backdrop-blur-sm"
                    >
                      <Icon className="w-5 h-5 mb-1" />
                      <p className="font-semibold">{opt.label}</p>
                      <p className="opacity-75 text-xs">
                        {opt.planLabel} · {opt.price}
                      </p>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Register;
